import { View } from 'react-native';
import { Text } from 'shared/components/text';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import {
  Caption,
  IllustrationFrame,
  Photo,
  useIllustrationCopy,
  type IllustrationProps,
} from './illustration-parts';

type Scaled = { s: IllustrationScale };

function CalendarGrid({ s }: Scaled) {
  const width = 112;
  const cellWidth = (width - 6) / 7;
  return (
    <View className="flex-row flex-wrap" style={{ width: s(width), gap: s(1) }}>
      {Array.from({ length: 14 }, (_, i) => {
        const day = i + 12;
        return (
          <View
            key={day}
            className="items-center justify-center"
            style={{
              width: s(cellWidth),
              height: s(cellWidth - 1),
              borderRadius: s(3),
              backgroundColor: day === 24 ? palette.forest : undefined,
            }}
          >
            <Text
              className="font-semibold"
              style={{
                color: day === 24 ? palette.paper : palette.ink,
                opacity: day === 24 ? 1 : 0.55,
                fontSize: s(7),
                lineHeight: s(10),
              }}
            >
              {day}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function CalendarBinding({ s }: Scaled) {
  return (
    <View
      className="absolute flex-row justify-between"
      style={{ left: s(17), right: s(17), top: s(-4) }}
    >
      {[0, 1, 2, 3].map((i) => (
        <View
          key={i}
          style={{
            width: s(3.5),
            height: s(11),
            borderWidth: s(1),
            borderColor: palette.ink + '90',
            borderRadius: s(2),
            backgroundColor: palette.creamDark,
          }}
        />
      ))}
    </View>
  );
}

function Appointment({ s }: Scaled) {
  const t = useIllustrationCopy();
  return (
    <View className="flex-row items-center" style={{ gap: s(7), width: s(112) }}>
      <Photo s={s} width={30} height={27} tone="clay" />
      <View style={{ flex: 1, gap: s(1) }}>
        <Caption s={s} size={6}>
          {t('pet')}
        </Caption>
        <Caption s={s} size={8}>
          {t('date')}
        </Caption>
        <Caption s={s} size={9} green>
          {t('time')}
        </Caption>
      </View>
    </View>
  );
}

function Rule({ s }: Scaled) {
  return <View style={{ height: s(0.7), backgroundColor: palette.ink + '25' }} />;
}

export function Pickup({ transparent = false }: IllustrationProps) {
  const t = useIllustrationCopy();
  return (
    <IllustrationFrame transparent={transparent}>
      {(s) => (
        <View
          className="absolute"
          style={{ left: s(13), top: s(17), width: s(134), height: s(129) }}
        >
          <View
            className="absolute inset-0 overflow-hidden"
            style={{
              padding: s(10),
              borderWidth: s(0.9),
              borderColor: palette.ink + '90',
              borderRadius: s(5),
              backgroundColor: palette.paper,
            }}
          >
            <View style={{ gap: s(6) }}>
              <Caption s={s} size={10}>
                {t('pickup')}
              </Caption>
              <CalendarGrid s={s} />
              <Rule s={s} />
              <Appointment s={s} />
            </View>
          </View>
          <CalendarBinding s={s} />
        </View>
      )}
    </IllustrationFrame>
  );
}
